import { useState } from "react";
import { api } from "../api";
import { ApiError, type PersonMembership } from "../api/types";
import { Button } from "../compose/controls";
import { LibraryBig, MessagesSquare } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import { initials, roleLabel } from "../lib/format";
import { ROLE_OPTIONS } from "../lib/roles";

/**
 * The member's article-unit contribution chips (People sweep §5): "N articles"
 * (distinct units touched) + "N open" (open comments authored). Rendered ONLY for
 * the counts that are present AND non-zero — a member with no article activity
 * stays the clean read-only card (design "softest reframe"). The counts are
 * optional on `PersonMembership`; a role-change response omits them ⇒ no chip.
 */
function ContributionChips({ person }: { person: PersonMembership }) {
  const units = person.article_unit_count ?? 0;
  const open = person.open_comment_count ?? 0;
  if (units <= 0 && open <= 0) return null;
  return (
    <div
      className="person__contrib"
      data-testid={`member-contrib-${person.user.id}`}
    >
      {units > 0 ? (
        <span
          className="contrib-chip"
          data-testid={`contrib-units-${person.user.id}`}
          title={`Active on ${units} article ${units === 1 ? "unit" : "units"}`}
        >
          <LibraryBig size={13} strokeWidth={1.5} aria-hidden />
          {units} {units === 1 ? "article" : "articles"}
        </span>
      ) : null}
      {open > 0 ? (
        <span
          className="contrib-chip contrib-chip--open"
          data-testid={`contrib-open-${person.user.id}`}
          title={`${open} open ${open === 1 ? "comment" : "comments"} authored`}
        >
          <MessagesSquare size={13} strokeWidth={1.5} aria-hidden />
          {open} open
        </span>
      ) : null}
    </div>
  );
}

/**
 * One member row on the People page. For a STAFF manager it exposes an inline
 * role picker + a two-step Remove control (design brief: "per-member role change
 * + remove-access"); for everyone else — and for the manager's OWN row — it stays
 * the read-only avatar/name/email/role card. The server is authoritative: a
 * role change / removal is rejected there for a non-manager, for a self-target
 * (409), and for a non-member (404); this component only decides what to render.
 */
export function MemberRow({
  person,
  slug,
  canManage,
  currentUserId,
  onChanged,
}: {
  person: PersonMembership;
  slug: string;
  canManage: boolean;
  currentUserId: string;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [role, setRole] = useState(person.role);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const isSelf = person.user.id === currentUserId;
  // A member can never manage its OWN access (matches the server self-lockout
  // guard) — render self as read-only even for a manager.
  const manageable = canManage && !isSelf;

  async function changeRole(next: string) {
    const prev = role;
    setRole(next);
    setBusy(true);
    try {
      await api.changeMemberRole(slug, person.user.id, { role: next });
      toast.show(`${person.user.name} is now ${roleLabel(next)}`);
      onChanged();
    } catch (err) {
      setRole(prev); // revert the optimistic change on failure
      toast.show(
        err instanceof ApiError ? err.message : "Could not change the role.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.removeMember(slug, person.user.id);
      toast.show(`Removed ${person.user.name} from the workspace.`);
      onChanged();
    } catch (err) {
      toast.show(
        err instanceof ApiError ? err.message : "Could not remove access.",
      );
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="person" data-testid={`person-${person.user.id}`}>
      <span className="avatar" aria-hidden>
        {initials(person.user.name)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="person__name">{person.user.name}</div>
        <div className="person__email">{person.user.email}</div>
      </div>

      <ContributionChips person={person} />

      {manageable ? (
        <div
          style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}
          data-testid={`member-controls-${person.user.id}`}
        >
          <select
            className="field__select"
            style={{ width: "auto", minWidth: 132 }}
            value={role}
            disabled={busy}
            aria-label={`Role for ${person.user.name}`}
            data-testid={`role-select-${person.user.id}`}
            onChange={(e) => changeRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {confirming ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                data-testid={`remove-cancel-${person.user.id}`}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                data-testid={`remove-confirm-${person.user.id}`}
                onClick={remove}
              >
                Confirm remove
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              data-testid={`remove-member-${person.user.id}`}
              onClick={() => setConfirming(true)}
            >
              Remove
            </Button>
          )}
        </div>
      ) : (
        <span
          className="person__role badge badge--neutral"
          data-testid={`role-badge-${person.user.id}`}
        >
          {roleLabel(person.role)}
        </span>
      )}
    </div>
  );
}
